import {Ability, AbilityBuilder} from '@casl/ability'
import {useSelector} from 'react-redux'
import {store} from '../redux/store';

// Same key apiCore stores the session under. Duplicated rather than imported to
// keep this leaf module free of the api layer (apiCore pulls in axios, the
// analytics SDK and the refresh-token interceptor).
const SESSION_KEY = 'concierge_user';

const ability = new Ability()

// Declared before checkAbility uses it; defined below, once applyPermissions and
// buildRules exist.
let sync: () => void

const checkAbility = (action: any, subject: any) => {
    // Re-read the live store on every call. `store` is `export let store`,
    // assigned inside configureStore(), so whether this module's binding is
    // populated depends on WHEN the module first evaluated — and if it evaluated
    // first, the subscription below was registered against `undefined` and the
    // ability never updated again for the whole session. That is the reported
    // bug: after login an inventory list showed no action icons, and Ctrl+R
    // brought them back, because a reload re-evaluated this module with the
    // session already in place. Syncing here makes the answer correct whatever
    // the load order; the identity compare in applyPermissions keeps it cheap.
    sync()
    return ability.can(action, subject)
};

export default checkAbility;

const buildRules = (permissions: any) => {
    // @ts-ignore
    const {can, rules} = new AbilityBuilder()

    // This logic depends on how the
    // server sends you the permissions array
    if (permissions) {
        permissions.forEach((p: any) => {
            can('able', p)
        })
    }

    return rules
}

// Keep the ability in sync with auth state. Previously it was built once at
// module load — before login/store-rehydration — so checkAbility() reflected
// empty permissions for the whole session (hiding entitled controls). Rebuild
// whenever the user's permission set changes; the reference compare keeps this
// cheap on unrelated dispatches.
// Compared by VALUE, not identity: the stored-session fallback below parses a new
// array on every read, so an identity compare would rebuild the rules on every
// single CAN() call.
let lastKey: string | null = null
const applyPermissions = (permission: any) => {
    const key = Array.isArray(permission) ? permission.join('|') : ''
    if (key === lastKey) return
    lastKey = key
    // @ts-ignore
    ability.update(buildRules(permission))
}
// Redux first, the stored session as a fallback. Two login paths on the login
// page write the session WITHOUT dispatching to the store, so Auth.user can be
// null while the user is fully authenticated — in which case every gated control
// silently disappears. Reading the session too means a login path that forgets
// to dispatch can no longer take the whole permission set down with it.
let sessionRaw: string | null = null
let sessionPermission: any
const permissionFromSession = () => {
    // One getItem per call; JSON.parse only when the stored session changed.
    const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SESSION_KEY) : null
    if (raw !== sessionRaw) {
        sessionRaw = raw
        try {
            sessionPermission = raw ? JSON.parse(raw)?.permission : undefined
        } catch {
            sessionPermission = undefined
        }
    }
    return sessionPermission
}
// FILLS IN, never revokes. useSyncedPermissions() is authoritative for clearing
// rules (it reads the Provider's store, which is the one React renders against);
// this only supplies rules when it can see a live source. Without that split a
// call-time sync would wipe what the hook had just applied whenever the module's
// own `store` binding is empty.
sync = () => {
    const permission = store?.getState?.()?.Auth?.user?.permission ?? permissionFromSession()
    if (permission === undefined || permission === null) return
    applyPermissions(permission)
}
sync()
store?.subscribe?.(sync)

/**
 * Subscribe a component subtree to the permission set.
 *
 * `checkAbility()` reads a module-level mutable Ability that React knows nothing
 * about. The store subscription above keeps that object current, but mutating it
 * triggers no re-render — so a permission that arrives after a component's last
 * render stays invisible: the gated button is simply absent, with no error
 * anywhere, until something unrelated happens to re-render the page. That is
 * exactly the reported symptom — after logging in, an inventory list shows no
 * action icons, and Ctrl+R brings them back, because a reload rebuilds the
 * ability from the stored session at module load.
 *
 * Call this in an ancestor of anything gated by CAN(). Two things then hold:
 *
 *   1. `useSelector` re-renders that subtree whenever the permission array
 *      identity changes, so a refreshed grant reaches the UI immediately.
 *   2. The ability is rebuilt DURING this component's render, before React
 *      renders any child. The store subscription alone is a race: react-redux
 *      registers its subscriber when the Provider mounts, ahead of the one in
 *      this module, so its subscribers can re-render a frame before the rules
 *      are updated.
 *
 * Ported from the admin portal, where the same gate had the same bug.
 */
export const useSyncedPermissions = (): string[] => {
    const fromStore = useSelector((s: any) => s?.Auth?.user?.permission);
    const permission = fromStore ?? permissionFromSession();
    applyPermissions(permission);
    return Array.isArray(permission) ? permission : [];
};
